import { defineAsyncComponent, markRaw } from 'vue';

import { competitiveRouteNames, competitiveSources } from '@/app/competitive/constants';
import LDRouteGuard from '@/components/core/LDRouteGuard';
import { constants } from '@/config';
import { useTrackingStore } from '@/stores/tracking';

const PlatformTypeWrapper = markRaw(
  defineAsyncComponent(() => import('@/app/competitive/pages/PlatformTypeWrapper.vue')),
);

const App = () => import('@/app/competitive/App.vue');
const ViqCompetitive = () => import('@/app/instagram/pages/ViqCompetitive.vue');
const ViqTrends = () => import('@/app/instagram/pages/ViqTrends.vue');
const CompetitiveClusterDetail = () => import('@/app/instagram/pages/CompetitiveClusterDetail.vue');
const TrendEdit = () => import('@/app/instagram/components/TrendEdit.vue');
const ViqTrend = () => import('@/app/instagram/pages/ViqTrend.vue');
const BoardsIndex = () => import('@/app/boards/pages/BoardsIndex.vue');
const CompetitiveOverviewRoute = () =>
  import('@/app/competitive/pages/overview/CompetitiveOverviewRoute.vue');
const CompetitivePostsRoute = () =>
  import('@/app/competitive/pages/posts/CompetitivePostsRoute.vue');

const PAGE_LEVEL_TOP = `Competitors`;
const PAGE_LEVEL_VIQ = `Visual IQ`;
const PAGE_LEVEL_TRENDS = 'Trends';
const PAGE_LEVEL_BOARDS = `Boards`;
const PAGE_LEVEL_TYPE = {
  instagram: 'Instagram',
  twitter: 'Twitter',
  facebook: 'Facebook',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

export default {
  path: 'competitors',
  component: App,
  meta: {
    requiresAuth: true,
    analytics: PAGE_LEVEL_TOP,
  },
  children: [
    {
      name: 'competitors',
      path: '',
      redirect: { name: 'competitors.competitorOverview' },
    },
    {
      name: 'competitors.competitorOverview',
      path: 'competitor-overview',
      redirect: { name: 'competitors.instagram.competitorOverview' },
    },
    {
      name: 'competitors.competitorPosts',
      path: 'competitor-posts',
      redirect: { name: 'competitors.instagram.competitorPosts' },
    },
    {
      name: 'competitors.viq.competitive',
      path: 'viq/competitive',
      redirect: { name: 'competitors.instagram.viq.competitive' },
    },
    {
      name: 'competitors.viq.competitive.detail',
      path: 'viq/competitive/:id',
      redirect: { name: 'competitors.instagram.viq.competitive.detail' },
    },
    {
      name: 'competitors.viq.trends',
      path: 'viq/trends',
      redirect: { name: 'competitors.instagram.viq.trends' },
      children: [
        {
          name: 'competitors.viq.trend.new',
          path: 'new',
          redirect: { name: 'competitors.instagram.viq.trend.new' },
        },
      ],
    },
    {
      name: 'competitors.viq.trend',
      path: 'viq/trends/:id',
      redirect: { name: 'competitors.instagram.viq.trend' },
      children: [
        {
          name: 'competitors.viq.trend.edit',
          path: 'edit',
          redirect: { name: 'competitors.instagram.viq.trend.edit' },
        },
      ],
    },
    {
      name: competitiveRouteNames.instagram.main,
      path: 'instagram',
      component: PlatformTypeWrapper,
      redirect: { name: 'competitors.instagram.competitorOverview' },
      children: [
        {
          name: competitiveRouteNames.instagram.overview,
          path: 'overview',
          component: CompetitiveOverviewRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - Competitor Overview`,
            socialMediaType: competitiveSources.INSTAGRAM,
          },
        },
        {
          name: competitiveRouteNames.instagram.posts,
          path: 'posts',
          component: CompetitivePostsRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - Competitor Posts`,
            socialMediaType: competitiveSources.INSTAGRAM,
          },
        },
        {
          path: 'viq/competitive',
          children: [
            {
              name: competitiveRouteNames.instagram.viq.main,
              path: '',
              component: ViqCompetitive,
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - VIQ Competitive`,
                socialMediaType: competitiveSources.INSTAGRAM,
              },
            },
            {
              name: competitiveRouteNames.instagram.viq.detail,
              path: ':id',
              component: CompetitiveClusterDetail,
              beforeEnter(to, from, next) {
                const trackingData = to.params?.data?.mixpanelObject;
                if (trackingData) {
                  const trackingStore = useTrackingStore();
                  trackingStore.track('Competitor View Competitor', trackingData);
                }
                next();
              },
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - View VIQ Competitive`,
                socialMediaType: competitiveSources.INSTAGRAM,
              },
            },
          ],
        },
        {
          path: 'viq/trends',
          children: [
            {
              name: competitiveRouteNames.instagram.viq.trends.main,
              path: '',
              component: ViqTrends,
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - VIQ Trends`,
                socialMediaType: competitiveSources.INSTAGRAM,
              },
              children: [
                {
                  name: competitiveRouteNames.instagram.viq.trends.new,
                  path: 'new',
                  component: TrendEdit,
                  meta: {
                    analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - ${PAGE_LEVEL_VIQ} - ${PAGE_LEVEL_TRENDS} - New`,
                    socialMediaType: competitiveSources.INSTAGRAM,
                  },
                },
              ],
            },
            {
              name: competitiveRouteNames.instagram.viq.trend.main,
              path: ':id',
              component: ViqTrend,
              beforeEnter(to, from, next) {
                const trackingData = to.params?.data?.mixpanelObject;
                if (trackingData) {
                  const trackingStore = useTrackingStore();
                  trackingStore.track('Competitor View Trend', trackingData);
                }
                next();
              },
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - View VIQ Trend Detail`,
                socialMediaType: competitiveSources.INSTAGRAM,
              },
              children: [
                {
                  name: competitiveRouteNames.instagram.viq.trend.edit,
                  path: 'edit',
                  component: TrendEdit,
                  meta: {
                    analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - ${PAGE_LEVEL_VIQ} - ${PAGE_LEVEL_TRENDS} - Edit`,
                    socialMediaType: competitiveSources.INSTAGRAM,
                  },
                },
              ],
            },
          ],
        },
        {
          path: 'boards',
          children: [
            {
              name: competitiveRouteNames.instagram.galleries.main,
              path: '',
              component: BoardsIndex,
              props: {
                galleryType: constants.INSTAGRAM,
                isCompetitors: true,
              },
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.instagram} - ${PAGE_LEVEL_BOARDS}`,
                socialMediaType: competitiveSources.INSTAGRAM,
              },
            },
          ],
        },
      ],
    },
    {
      path: 'twitter:pathMatch(.*)*',
      redirect: (to) => ({ ...to, path: to.path.replace('/twitter', '/x') }),
    },
    {
      name: competitiveRouteNames.twitter.main,
      path: 'x',
      component: PlatformTypeWrapper,
      redirect: { name: 'competitors.twitter.competitorOverview' },
      children: [
        {
          name: competitiveRouteNames.twitter.overview,
          path: 'overview',
          component: CompetitiveOverviewRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.twitter} - Competitor Overview`,
            socialMediaType: competitiveSources.TWITTER,
          },
        },
        {
          name: competitiveRouteNames.twitter.posts,
          path: 'posts',
          component: CompetitivePostsRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.twitter} - Competitor Posts`,
            socialMediaType: competitiveSources.TWITTER,
          },
        },
        {
          path: 'boards',
          children: [
            {
              name: competitiveRouteNames.twitter.galleries.main,
              path: '',
              component: BoardsIndex,
              props: {
                galleryType: constants.TWITTER,
                isCompetitors: true,
              },
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.twitter} - ${PAGE_LEVEL_BOARDS}`,
                socialMediaType: competitiveSources.TWITTER,
              },
            },
          ],
        },
      ],
    },
    {
      name: competitiveRouteNames.facebook.main,
      path: 'facebook',
      component: PlatformTypeWrapper,
      redirect: { name: 'competitors.facebook.competitorOverview' },
      children: [
        {
          name: competitiveRouteNames.facebook.overview,
          path: 'overview',
          component: CompetitiveOverviewRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.facebook} - Competitor Overview`,
            socialMediaType: competitiveSources.FACEBOOK,
          },
        },
        {
          name: competitiveRouteNames.facebook.posts,
          path: 'posts',
          component: CompetitivePostsRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.facebook} - Competitor Posts`,
            socialMediaType: competitiveSources.FACEBOOK,
          },
        },
        {
          path: 'boards',
          children: [
            {
              name: competitiveRouteNames.facebook.galleries.main,
              path: '',
              component: BoardsIndex,
              props: {
                galleryType: constants.FACEBOOK,
                isCompetitors: true,
              },
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.facebook} - ${PAGE_LEVEL_BOARDS}`,
                socialMediaType: competitiveSources.FACEBOOK,
              },
            },
          ],
        },
      ],
    },
    {
      name: competitiveRouteNames.youtube.main,
      path: 'youtube',
      component: PlatformTypeWrapper,
      redirect: { name: 'competitors.youtube.competitorOverview' },
      children: [
        {
          name: competitiveRouteNames.youtube.overview,
          path: 'overview',
          component: CompetitiveOverviewRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.youtube} - Competitor Overview`,
            socialMediaType: competitiveSources.YOUTUBE,
          },
        },
        {
          name: competitiveRouteNames.youtube.posts,
          path: 'posts',
          component: CompetitivePostsRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.youtube} - Competitor Posts`,
            socialMediaType: competitiveSources.YOUTUBE,
          },
        },
        {
          path: 'boards',
          children: [
            {
              name: competitiveRouteNames.youtube.galleries.main,
              path: '',
              component: BoardsIndex,
              props: {
                galleryType: constants.YOUTUBE,
                isCompetitors: true,
              },
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.youtube} - ${PAGE_LEVEL_BOARDS}`,
                socialMediaType: competitiveSources.YOUTUBE,
              },
            },
          ],
        },
      ],
    },
    {
      name: competitiveRouteNames.tiktok.main,
      path: 'tiktok',
      component: LDRouteGuard,
      props: {
        component: PlatformTypeWrapper,
        requiredFeatureFlag: 'tiktokCompetitive',
        to: { name: 'competitors' },
      },
      redirect: { name: competitiveRouteNames.tiktok.overview },
      children: [
        {
          name: competitiveRouteNames.tiktok.overview,
          path: 'overview',
          component: CompetitiveOverviewRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.tiktok} - Competitor Overview`,
            socialMediaType: competitiveSources.TIKTOK,
          },
        },
        {
          name: competitiveRouteNames.tiktok.posts,
          path: 'posts',
          component: CompetitivePostsRoute,
          meta: {
            analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.tiktok} - Competitor Posts`,
            socialMediaType: competitiveSources.TIKTOK,
          },
        },
        {
          path: 'boards',
          children: [
            {
              name: competitiveRouteNames.tiktok.galleries.main,
              path: '',
              component: BoardsIndex,
              props: {
                galleryType: constants.TIKTOK,
                isCompetitors: true,
              },
              meta: {
                analytics: ` ${PAGE_LEVEL_TOP} - ${PAGE_LEVEL_TYPE.tiktok} - ${PAGE_LEVEL_BOARDS}`,
                socialMediaType: competitiveSources.TIKTOK,
              },
            },
          ],
        },
      ],
    },
  ],
};
